import { Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import ReactECharts, { AXIS_TEXT, GRID_LINE } from '../charts/echarts';
import type { CreatorFacts, MonthTotals } from '../contract';
import { fmtCompact, fmtDay, fmtHours, fmtKc, fmtMonth, fmtNum, secondsToHours } from '../format';
import styles from '../style.module.scss';

interface DayRow {
  day: string;
  diamonds: number;
  liveSeconds: number;
  liveDays: number;
  newFollowers: number;
  overseas: number | null;
  newFans: number | null;
  sessions: number | null;
  pk: number | null;
}

type MoreSeries = NonNullable<CreatorFacts['more']>['series'];

/** Giá trị ngày i của chuỗi phụ — chỉ khi chuỗi khớp độ dài `days` (không khớp → không đoán). */
const at = (arr: number[] | undefined, len: number, i: number): number | null =>
  arr && arr.length === len ? arr[i] ?? null : null;

const numCell = (v: number | null) => (v === null ? '—' : fmtNum(v));

/**
 * Bảng 30 ngày (mới → cũ) từ facts.series (+ facts.more.series đợt 1b: KC quốc tế, fan mới, phiên, KC PK).
 * Cột phụ chỉ hiện khi chuỗi phụ có số (ảnh chụp cũ không có → ẩn).
 */
export const DailyTable = ({
  series,
  more,
}: {
  series: CreatorFacts['series'];
  more?: MoreSeries;
}) => {
  const len = series.days.length;
  const rows: DayRow[] = useMemo(
    () =>
      series.days
        .map((day, i) => ({
          day,
          diamonds: series.diamonds[i] || 0,
          liveSeconds: series.liveSeconds[i] || 0,
          liveDays: series.liveDays[i] || 0,
          newFollowers: series.newFollowers[i] || 0,
          overseas: at(more?.overseasDiamonds, len, i),
          newFans: at(more?.newFans, len, i),
          sessions: at(more?.liveSessions, len, i),
          pk: at(more?.pkDiamonds, len, i),
        }))
        .reverse()
        .slice(0, 30),
    [series, more, len]
  );
  const hasMore = !!more && more.overseasDiamonds.length === len && len > 0;
  const columns: ColumnsType<DayRow> = [
    { title: 'Ngày', dataIndex: 'day', width: 64, fixed: 'left', render: (v: string) => fmtDay(v) },
    { title: 'KC', dataIndex: 'diamonds', align: 'right', render: (v: number) => fmtKc(v) },
    {
      title: 'Giờ LIVE',
      dataIndex: 'liveSeconds',
      align: 'right',
      render: (v: number) => fmtHours(secondsToHours(v)),
    },
    {
      title: 'Ngày hợp lệ',
      dataIndex: 'liveDays',
      align: 'center',
      render: (v: number) => (v > 0 ? 'Có' : <span className={styles.muted}>Không</span>),
    },
    {
      title: 'Follower mới',
      dataIndex: 'newFollowers',
      align: 'right',
      render: (v: number) => fmtNum(v),
    },
  ];
  if (hasMore) {
    columns.push(
      { title: 'KC quốc tế', dataIndex: 'overseas', align: 'right', render: numCell },
      { title: 'Fan mới', dataIndex: 'newFans', align: 'right', render: numCell },
      { title: 'Phiên', dataIndex: 'sessions', align: 'right', render: numCell },
      { title: 'KC PK', dataIndex: 'pk', align: 'right', render: numCell }
    );
  }
  return (
    <section className={styles.card} aria-label="Bảng theo ngày">
      <h2 className={styles.cardTitle}>Từng ngày</h2>
      <Table<DayRow>
        size="small"
        rowKey="day"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: hasMore ? 760 : 420, y: 320 }}
      />
    </section>
  );
};

/** Lịch sử ≤ 3 tháng trước + tháng đang xem (cột nhạt hơn, ghi "đến ngày …"). */
export const HistoryBars = ({
  history,
  current,
}: {
  history: MonthTotals[];
  current: CreatorFacts;
}) => {
  const option = useMemo(() => {
    const items = history.map((h) => ({
      label: fmtMonth(h.month),
      diamonds: h.diamonds,
      hours: secondsToHours(h.liveSeconds),
      days: h.liveDays,
      partial: false,
    }));
    items.push({
      label: `${fmtMonth(current.month)} (đến ${fmtDay(current.asOfDay)})`,
      diamonds: current.totals.diamonds,
      hours: secondsToHours(current.totals.liveSeconds),
      days: current.totals.liveDays,
      partial: true,
    });
    return {
      animation: false,
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => {
          const it = items[p.dataIndex];
          return it
            ? `${it.label}<br/>${fmtKc(it.diamonds)} KC · ${fmtHours(it.hours)} · ${fmtNum(
                it.days
              )} ngày hợp lệ`
            : '';
        },
      },
      xAxis: {
        type: 'category',
        data: items.map((it) => it.label.replace('Tháng ', 'T')),
        axisLabel: { color: AXIS_TEXT, interval: 0 },
        axisLine: { lineStyle: { color: GRID_LINE } },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: { color: AXIS_TEXT, formatter: (v: number) => fmtCompact(v) },
        splitLine: { lineStyle: { color: GRID_LINE } },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 40,
          data: items.map((it) => ({
            value: it.diamonds,
            itemStyle: {
              color: it.partial ? '#EE93CA' : '#C2337D',
              borderRadius: [4, 4, 0, 0],
            },
          })),
          label: {
            show: true,
            position: 'top',
            color: AXIS_TEXT,
            formatter: (p: any) => fmtCompact(p.value),
          },
        },
      ],
    };
  }, [history, current]);

  return (
    <section className={styles.card} aria-label="Lịch sử 3 tháng">
      <h2 className={styles.cardTitle}>Lịch sử Kim cương</h2>
      {history.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có số liệu 3 tháng trước."
          className={styles.compactEmpty}
        />
      )}
      <ReactECharts option={option} style={{ height: 220, width: '100%' }} />
    </section>
  );
};
